// Fixed-timestep simulation loop with interpolated rendering (GDD §8.2).
// The sim advances in discrete 40ms ticks (25 tps); the renderer draws at display
// refresh rate and interpolates between the previous and current sim state.

export const TICK_RATE = 25;
export const TICK_MS = 1000 / TICK_RATE; // 40ms
export const TICK_DT = 1 / TICK_RATE; // 0.04s — pass this to sim as fixed dt

export interface LoopHandlers {
  /** Advance the simulation by exactly one fixed tick (TICK_DT seconds). */
  tick(): void;
  /** Draw. `alpha` in [0,1] interpolates between previous and current sim state. */
  render(alpha: number): void;
}

export class GameLoop {
  private handlers: LoopHandlers;
  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  private running = false;
  /** simulation speed multiplier (1 = realtime). */
  speed = 1;
  /** rolling ticks-per-second estimate for the HUD. */
  measuredTps = TICK_RATE;
  private tpsWindow: number[] = [];

  constructor(handlers: LoopHandlers) {
    this.handlers = handlers;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    const frame = (now: number) => {
      if (!this.running) return;
      let frameTime = now - this.lastTime;
      this.lastTime = now;
      // Guard against huge dt after tab-switch / breakpoint.
      if (frameTime > 250) frameTime = 250;
      this.accumulator += frameTime * this.speed;

      let ticks = 0;
      while (this.accumulator >= TICK_MS) {
        this.handlers.tick();
        this.accumulator -= TICK_MS;
        if (++ticks > 8) {
          this.accumulator = 0; // spiral-of-death guard
          break;
        }
      }
      if (ticks > 0) {
        this.tpsWindow.push(ticks);
        if (this.tpsWindow.length > 60) this.tpsWindow.shift();
        const sum = this.tpsWindow.reduce((a, b) => a + b, 0);
        this.measuredTps = (sum / this.tpsWindow.length) * (1000 / 16.67);
      }

      const alpha = this.accumulator / TICK_MS;
      this.handlers.render(alpha);
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  stop(): void {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }
}
